import React, {FC, useState, useCallback, useEffect, RefObject} from "react";
import {Box, Chip, TextField} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import SendIcon from "@mui/icons-material/Send";

export interface VideoPlayerRef {
	bitmovin?: { player?: { getCurrentTime: () => number } };
	react?: { getCurrentTime: () => number };
	mux?: { currentTime: number };
	brightcove?: { player?: { currentTime: () => number } };
}

const useStyles = makeStyles()((theme) => ({
	input: {
		width: "100%"
	},
	icon: {
		cursor: "pointer"
	},
	timeStampInputAdornment: {
		marginRight: theme.spacing(1.5)
	},
	sendButtonInputAdornment: {
		marginTop: theme.spacing(0.75),
		alignSelf: "end"
	}
}));

interface NoteInputProps {
	postNote: (note: { content: string; videoTimestamp?: number }) => void;
	placeholder?: string;
	videoRef?: RefObject<VideoPlayerRef | null>;
	disableTimeStamp?: boolean;
	className?: string;
}

export const NoteInput: FC<NoteInputProps> = ({
	postNote,
	placeholder = "Type your private note",
	disableTimeStamp,
	className,
	videoRef
}) => {
	const {classes, cx} = useStyles();
	const [note, setNote] = useState("");
	const [videoTime, setVideoTime] = useState(0);

	const formatTimestamp = (timestamp: number): string => {
		if (timestamp === 0) return "00:00:00";
		const hours = Math.floor(timestamp / 3600);
		const minutes = Math.floor((timestamp % 3600) / 60);
		const seconds = Math.floor(timestamp % 60);
		return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
	};

	const getVideoTimestamp = useCallback((): number | undefined => {
		if (videoRef?.current?.bitmovin?.player) return videoRef.current.bitmovin.player.getCurrentTime();
		if (videoRef?.current?.react) return videoRef.current.react.getCurrentTime();
		if (videoRef?.current?.mux) return videoRef.current.mux.currentTime;
		if (videoRef?.current?.brightcove?.player) return videoRef.current.brightcove.player.currentTime();
		return undefined;
	}, [videoRef]);

	useEffect(() => {
		const interval = setInterval(() => {
			const ts = getVideoTimestamp();
			if (ts !== undefined && ts !== 0) setVideoTime(ts);
			else setVideoTime(0);
		}, 100);
		return () => clearInterval(interval);
	}, [getVideoTimestamp]);

	const handleSubmit = useCallback(() => {
		if (!note.trim()) return;
		const payload: { content: string; videoTimestamp?: number } = { content: note.trim() };
		if (videoTime > 0) payload.videoTimestamp = videoTime;
		postNote(payload);
		setNote("");
	}, [note, videoTime, postNote]);

	return (
		<TextField
			className={cx(classes.input, className)}
			placeholder={placeholder}
			data-test-id="note-input"
			size="small"
			multiline
			InputProps={{
				disableUnderline: true,
				autoCapitalize: "none",
				autoComplete: "off",
				size: "small",
				startAdornment:
					disableTimeStamp !== true && videoRef?.current ? (
						<Box className={classes.timeStampInputAdornment}>
							<Chip color="secondary" label={formatTimestamp(videoTime)} />
						</Box>
					) : null,
				endAdornment: (
					<Box className={classes.sendButtonInputAdornment} role="button" tabIndex={0} onClick={handleSubmit}>
						<SendIcon className={classes.icon} data-test-id="note-input-submit-btn" />
					</Box>
				)
			}}
			value={note}
			onChange={(e) => setNote(e.target.value)}
		/>
	);
};
